import { FreshMealPlanType } from "libs/api/customer/src/lib/types/checkout";
import { ICapiCustomData } from "@capi/types";
import { IGetPriceEstimateResponse, GTMProduct } from "@api-clients/checkout";

import { PricingPlan, SubscriptionItemType } from "@/primary-models";

import { ISignupFlowStore } from "./types";

export interface IFreshPricingExtras {
  product: string;
  quantity: number;
  trial: { price: string }
  ongoing: { price: string }
}

export interface IFreshPricingDelivery {
  ongoing: string;
  trial: string;
  date: string;
}

export interface IFreshPricingPerCatPricing {
  id: number;
  name: string;
  ongoing: {
    pricePerDay: string;
  };
  trial: {
    pricePerDay: string;
  };
}

export interface IFreshPricingDisplay {
  trial: {
    planType: PricingPlan;
    originalTotal: string;
    discountedTotal: string;
    originalPerCatPerDay: string;
    discountedPerCatPerDay: string;
    originalPricePerPortion: string;
    discountedPricePerPortion: string;
  };
  ongoing: {
    planType: PricingPlan;
    originalTotal: string;
    discountedTotal: string | undefined;
    originalPerCatPerDay: string;
    discountedPerCatPerDay: string | undefined;
    originalPricePerPortion: string;
    discountedPricePerPortion: string | undefined;
  };
  delivery: IFreshPricingDelivery
  nibblesUpsell?: IFreshPricingExtras;
  sprinklesUpsell?: IFreshPricingExtras;
  discount?: {
    code: string;
  };
  perCatPricing: IFreshPricingPerCatPricing[]
}
export const mapFreshOptionsResponseToPricingDisplay = (
  estimate: IGetPriceEstimateResponse,
): IFreshPricingDisplay => {
  if (estimate.cats.length === 0) {
    const emptySubscription = {
      planType: PricingPlan.TRIAL,
      originalTotal: "0.00",
      discountedTotal: "0.00",
      originalPerCatPerDay: "0.00",
      discountedPerCatPerDay: "0.00",
      originalPricePerPortion: "0.00",
      discountedPricePerPortion: "0.00",
    };
    return {
      trial: emptySubscription,
      ongoing: emptySubscription,
      nibblesUpsell: undefined,
      sprinklesUpsell: undefined,
      discount: undefined,
      delivery: {
        ongoing: "0.00",
        trial: "0.00",
        date: "",
      },
      perCatPricing: [],
    };
  }

  const {
    totals,
    cats,
    discount,
    deliveryDate,
  } = estimate;
  const { code } = discount ?? {};

  const extras = cats.flatMap((cat) => cat.trial.extras).map((extra) => ({
    product: extra.productTitle,
    quantity: extra.quantity,
    ongoing: {
      price: extra.unitPrice,
    },
    trial: {
      price: extra.discountedPrice,
    },
  }));

  const nibblesUpsell = extras.find((extra) => extra.product === SubscriptionItemType.NIBBLES_CHICKEN
  || extra.product === SubscriptionItemType.NIBBLES_SALMON);

  const sprinklesUpsell = extras.find((extra) => extra.product === SubscriptionItemType.SPRINKLES);

  const perCatPricing = cats.map((cat, index) => ({
    id: cat.catId ?? index,
    name: cat.catName,
    ongoing: {
      pricePerDay: cat.ongoing.discounted.mealPlan.perDay,
    },
    trial: {
      pricePerDay: cat.trial.discounted.mealPlan.perDay,
    },
  }));

  return {
    trial: {
      planType: cats[0].trial.planType,
      originalTotal: totals.trial.original.overall.total,
      discountedTotal: totals.trial.discounted.overall.total,
      originalPerCatPerDay: totals.trial.original.mealPlan.perCatPerDay ?? "0.00",
      discountedPerCatPerDay: totals.trial.discounted.mealPlan.perCatPerDay ?? "0.00",
      originalPricePerPortion: totals.trial.original.mealPlan.perPortion ?? "0.00",
      discountedPricePerPortion: totals.trial.discounted.mealPlan.perPortion ?? "0.00",
    },
    ongoing: {
      planType: cats[0].ongoing.planType,
      originalTotal: totals.ongoing.original.overall.total,
      discountedTotal: totals.ongoing.discounted.overall.total,
      originalPerCatPerDay: totals.ongoing.original.mealPlan.perCatPerDay ?? "0.00",
      discountedPerCatPerDay: totals.ongoing.discounted.mealPlan.perCatPerDay,
      originalPricePerPortion: totals.ongoing.original.mealPlan.perPortion ?? "0.00",
      discountedPricePerPortion: totals.ongoing.discounted.mealPlan.perPortion,
    },
    delivery: {
      ongoing: totals.ongoing.original.delivery.total,
      trial: totals.trial.original.delivery.total,
      date: deliveryDate,
    },
    nibblesUpsell,
    sprinklesUpsell,
    perCatPricing,
    discount: code ? {
      code,
    } : undefined,
  };
};

export const transformCats = (cats: ISignupFlowStore["cats"], perCatPricing: IFreshPricingPerCatPricing[]) => {
  const updatedCats: ISignupFlowStore["cats"] = { ...cats };
  for (const catId of Object.keys(updatedCats)) {
    const cat = updatedCats[catId];
    const catPricing = perCatPricing.find((pc) => pc.name.toLowerCase() === cat.name?.toLowerCase());

    updatedCats[catId] = {
      ...cat,
      trialPricePerDay: catPricing?.trial.pricePerDay,
      ongoingPricePerDay: catPricing?.ongoing.pricePerDay,
    };
  }
  return updatedCats;
};

/**
 * Helper function to categorise a GTM product
 */
const categoriseGTMProduct = (gtmProduct: GTMProduct): string => {
  if (gtmProduct.name.includes("Delivery")) {
    return "Delivery Fee";
  }
  if (gtmProduct.name === "Sprinkles") {
    return "extra";
  }
  if (gtmProduct.id.includes("mealplan")) {
    return "meal_plan";
  }
  return "recipe";
};

/**
 * Creates CAPI custom data from Fresh Options Response for tracking purposes.
 * Uses the gtmProducts array which contains all items (recipes, extras, delivery, meal plan).
 */
export const createCapiCustomDataFromFreshOptions = (
  response: IGetPriceEstimateResponse,
  product: FreshMealPlanType = "FRESH_TRIAL",
): ICapiCustomData => {
  const { totals, discount, gtmProducts } = response;

  // Map gtmProducts directly to CAPI contents format
  const contents = gtmProducts?.map((gtmProduct) => ({
    id: gtmProduct.id,
    quantity: gtmProduct.quantity,
    itemPrice: Number.parseFloat(gtmProduct.price) || 0,
    title: gtmProduct.name,
    brand: gtmProduct.brand,
    deliveryCategory: "home_delivery" as const,
    category: categoriseGTMProduct(gtmProduct),
  })) || [];

  return {
    value: Number.parseFloat(totals.trial.discounted.overall.total),
    currency: "gbp",
    contentName: product,
    contentCategory: "fresh",
    contents,
    contentType: "product",
    numItems: response.cats.length,
    searchString: discount?.code,
    deliveryCategory: "home_delivery",
  };
};
